// TwoColumnDescriptionList.js

import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from 'react-toastify';
import {
  fetchAppointmentsByClientId,
  notifyClientByEmail,
  updateAppointmentStatus,
  updateAppointment,
  updateAppointmentHistory,
  fetchAppointmentById,
  revertTreated,
} from "../../store/thunks/appointmentsThunk";
import { fetchParents } from "../../store/thunks/clientsThunk";
import { getBlock } from "../../store/thunks/blocksThunk";
import { clearBlockState } from "../../store/slices/blocksSlice";
import { calculateAge } from "../../utils/calculateAge";
import CancelAppointmentModal from "./CancelAppointmentModal";
import NoShowAppointmentModal from "./NoShowAppointmentModal";
import SOAPForm from "./SOAPForm";
import AppointmentButtonContainer from "./AppointmentButtonContainer";
import RescheduleModal from "./RescheduleModal";
import StatusLights from "./StatusLights";
import FamilyDetails from "../../pages/clientsPage/FamilyDetails";
import QuickAddAppointmentModal from "../../modals/QuickAddAppointmentModal";
import ModifyBlockModal from "../../modals/ModifyBlockModal";
import AddToBlockModal from "./AddToBlockModal";
import TreatmentModal from "./TreatmentModal";
import ModifyResourceModal from "../../modals/ModifyResourceModal";
import EditStatusModal from "./EditStatusModal";
import ModifyClinicianModal from "../../modals/ModifyClinicianModal";

export default function TwoColumnDescriptionList({
  type,
  appointment,
  client,
  refetchAppointments,
  onAppointmentCancel,
  onEmailSent,
}) {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const [showSOAPForm, setShowSOAPForm] = useState(false);
  const [soapFormAppointment, setSoapFormAppointment] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showNoShowModal, setShowNoShowModal] = useState(false);
  const [showQuickAddModal, setShowQuickAddModal] = useState(false);
  const [showModifyBlockModal, setShowModifyBlockModal] = useState(false);
  const [showModifyStandaloneModal, setShowModifyStandaloneModal] =
    useState(false);
  const [showModifyResourceModal, setShowModifyResourceModal] = useState(false);
  const [showParentsComponent, setShowParentsComponent] = useState(false);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [rescheduleAppointment, setRescheduleAppointment] = useState(null);
  const [localBlockData, setLocalBlockData] = useState(null);
  const [showEditStatusModal, setShowEditStatusModal] = useState(false);
  const [showModifyClinicianModal, setShowModifyClinicianModal] = useState(false);
  const [clinicianModalType, setClinicianModalType] = useState(null); // "treating" or "supervising"

  const [localParents, setLocalParents] = useState([]);
  const [showTreatmentModal, setShowTreatmentModal] = useState(false);
  const [clinicianCancelComments, setClinicianCancelComments] = useState("");

  const selectedAppointment = useSelector(
    (state) => state.appointments.selectedAppointment || appointment
  );

  console.log(
    "[TWOCOLUMNDESCRIPTIONLIST] - selectedAppointment:",
    selectedAppointment
  );

  console.log("[TWOCOLUMNDESCRIPTIONLIST] - user:", user);

  useEffect(() => {
    if (selectedAppointment && selectedAppointment.client) {
      dispatch(fetchAppointmentsByClientId(selectedAppointment.client._id));
    }
  }, [dispatch]);

  useEffect(() => {
    if (selectedAppointment) {
      dispatch(fetchAppointmentById(selectedAppointment._id));
    }
  }, [dispatch]);

  useEffect(() => {
    if (appointment?.blockId) {
      dispatch(getBlock(appointment.blockId))
        .then((response) => {
          setLocalBlockData(response.payload);
        })
        .catch((error) => {
          console.error("Failed to fetch block data:", error);
        });
    }
  }, [dispatch, appointment?.blockId]);

  if (type === "client" && !client) {
    return <p>No client information available.</p>;
  }

  if (type === "appointment" && !appointment) {
    return <p>No appointment information available.</p>;
  }

  const handleButtonClick = (action) => {
    if (action.startsWith("status-")) {
      const nextStatus = action.split("status-")[1];

      // ✅ Special case: Handle "Treated" separately
      if (nextStatus === "Treated") {
        handleMarkAsTreated();
        // Remove duplicate - handleSubmitAttendance will handle the status update
        // handleNextStatus();
        return; // Stop further execution
      }

      handleNextStatus(nextStatus);
    } else {
      switch (action) {
        case "cancel":
          handleCancel();
          break;
        case "noShow":
          handleNoShow();
          break;
        case "notify":
          handleNotifyClient();
          break;
        case "modifyAppointment":
          handleModifyAppointment();
          break;
        case "manageResources":
          handleManageResources();
          break;
        case "createSOAPNote":
          handleCreateSOAPNote();
          break;
        case "modifyBlock":
          setShowModifyBlockModal(true);
          break;
        case "addToBlock":
          setShowModifyStandaloneModal(true);
          break;
        case "revertTreated":
          handleRevertTreated();
          break;
        case "rescheduleAppointment":
          handleRescheduleAppointment();
          break;
        default:
          console.warn(`Unknown button action: ${action}`);
      }
    }
  };

  const handleMarkAsTreated = async () => {
    if (!appointment?.client?._id) return;

    try {
      const response = await dispatch(
        fetchParents(appointment.client._id)
      ).unwrap();

      setLocalParents(response || []);
      setShowTreatmentModal(true);
    } catch (error) {
      console.error("Failed to fetch parents:", error);
      toast.error("An error occurred while fetching parents.");
    }
  };

  const handleSubmitAttendance = async (
    selectedParents = [],
    billingParents = [],
    attendanceData = {}
  ) => {
    if (!appointment) return;

    // Extract attendance details
    const { noParentAttended, manualParentName } = attendanceData;

    // Handle the skip option
    if (selectedParents.length === 0 && billingParents.length === 0 && !noParentAttended && !manualParentName) {
      const historyItem = {
        status: "Treated",
        timestamp: new Date(),
        comments: "Attendance / split billing option has been skipped.",
        user: user._id,
      };

      try {
        await dispatch(
          updateAppointmentHistory({ id: appointment._id, historyItem })
        ).unwrap();

        // Update the status array and refresh the appointment data
        await dispatch(updateAppointment({
          id: appointment._id,
          updatedData: {
            status: [...(appointment.status || []).filter(s => s !== "Treated"), "Treated"]
          }
        })).unwrap();

        setShowTreatmentModal(false);
      } catch (error) {
        console.error("Failed to update history:", error);
      }
      return;
    }

    // Format Attendance Details based on selection type
    let formattedAttendance;
    if (noParentAttended) {
      formattedAttendance = "No parent attended";
    } else if (manualParentName) {
      formattedAttendance = manualParentName;
    } else {
      formattedAttendance = selectedParents.length
        ? selectedParents.map((p) => `${p.firstName} ${p.lastName}`).join(", ")
        : "None";
    }

    // Format Split Billing Details (only applicable for linked parents)
    const formattedBilling = billingParents.length
      ? billingParents.map((p) => `${p.firstName} ${p.lastName}`).join(", ")
      : "Not Split";

    // Construct History Item
    const historyItem = {
      status: "Treated",
      timestamp: new Date(),
      comments: `Parents attended: ${formattedAttendance}. Split billing with: ${formattedBilling}.`,
      user: user._id,
    };

    console.log("[TwoColumnDescriptionList] Submitting History Item:", historyItem);

    try {
      await dispatch(
        updateAppointmentHistory({ id: appointment._id, historyItem })
      ).unwrap();

      // Update the status array and refresh the appointment data
      await dispatch(updateAppointment({
        id: appointment._id,
        updatedData: {
          status: [...(appointment.status || []).filter(s => s !== "Treated"), "Treated"]
        }
      })).unwrap();

      setShowTreatmentModal(false);
      toast.success("Attendance recorded successfully");
    } catch (error) {
      console.error("[TwoColumnDescriptionList] Failed to update history:", error);
      toast.error("Failed to record attendance");
    }
  };

  const handleNextStatus = (nextStatus) => {
    if (nextStatus) {
      dispatch(
        updateAppointmentStatus({
          appointmentId: appointment._id,
          status: nextStatus,
          comments: `Status changed to ${nextStatus}`,
          userId: user._id,
        })
      );
    } else {
      console.log("No further status available");
    }
  };

  const handleCreateSOAPNote = () => {
    setSoapFormAppointment(appointment);
    setShowSOAPForm(true);
  };

  const handleRevertTreated = () => {
    dispatch(revertTreated({ appointmentId: appointment._id }));
  };

  const handleManageResources = () => {
    console.log("Managing resources for appointment", appointment);
    setShowModifyResourceModal(true);
  };

  const handleModifyAppointment = () => {
    setShowQuickAddModal(true);
    dispatch(fetchAppointmentById(selectedAppointment._id));
  };

  const handleCancel = () => {
    console.log("Cancelling appointment", appointment);
    setShowCancelModal(true);
  };

  const handleNoShow = () => {
    console.log("No showing appointment", appointment);
    setShowNoShowModal(true);
  };

  const handleRescheduleAppointment = () => {
    console.log("Rescheduling appointment", appointment);
    setRescheduleAppointment(appointment._id);
    setShowRescheduleModal(true);
  };

  const handleClinicianClick = (type) => {
    setClinicianModalType(type);
    setShowModifyClinicianModal(true);
  };

  const handleConfirmCancelNoFee = async () => {
    if (!appointment) return;
    const validStatus = "Cancelled - No Fee";
    const updatedData = {
      status: [validStatus],
      history: [
        ...appointment.history,
        {
          status: validStatus,
          timestamp: new Date(),
          comments: "Appointment cancelled - No Fee",
          user: user._id,
        },
      ],
    };

    try {
      await dispatch(
        updateAppointment({
          id: appointment._id,
          updatedData,
        })
      ).unwrap();

      setClinicianCancelComments("");
      setShowCancelModal(false);
      onAppointmentCancel();
    } catch (error) {
      console.error("Failed to cancel appointment:", error);
    }
  };

  const handleConfirmCancelWithFee = async () => {
    if (!appointment) return;

    const validStatus = "Cancelled - Charge Fee";
    const updatedData = {
      status: [validStatus],
      history: [
        ...appointment.history,
        {
          status: validStatus,
          timestamp: new Date(),
          comments: "Appointment cancelled - Charge Fee",
          user: user._id,
        },
      ],
    };

    try {
      await dispatch(
        updateAppointment({
          id: appointment._id,
          updatedData,
        })
      ).unwrap();

      setClinicianCancelComments("");
      setShowCancelModal(false);
      onAppointmentCancel();
    } catch (error) {
      console.error("Failed to cancel appointment:", error);
    }
  };

  const handleClinicianCancel = async () => {
    if (!appointment) return;

    const validStatus = "Cancelled - Clinician";
    const updatedData = {
      status: [validStatus],
      history: [
        ...appointment.history,
        {
          status: validStatus,
          timestamp: new Date(),
          comments:
            clinicianCancelComments || "Appointment cancelled by clinician",
          user: user._id,
        },
      ],
    };

    try {
      await dispatch(
        updateAppointment({
          id: appointment._id,
          updatedData,
        })
      ).unwrap();

      setClinicianCancelComments("");
      setShowCancelModal(false);
      onAppointmentCancel();
    } catch (error) {
      console.error("Failed to cancel appointment:", error);
    }
  };

  const handleConfirmNoShowNoFee = async () => {
    if (!appointment) return;

    const validStatus = "No Show - No Fee";
    const updatedData = {
      status: [validStatus],
      history: [
        ...appointment.history,
        {
          status: validStatus,
          timestamp: new Date(),
          comments: "Appointment marked as No Show - No Fee",
          user: user._id,
        },
      ],
    };

    try {
      await dispatch(
        updateAppointment({
          id: appointment._id,
          updatedData,
        })
      ).unwrap();

      setShowNoShowModal(false);
      onAppointmentCancel();
    } catch (error) {
      console.error("Failed to mark appointment as No Show:", error);
    }
  };

  const handleConfirmNoShowWithFee = async () => {
    if (!appointment) return;

    const validStatus = "No Show - Charge Fee";
    const updatedData = {
      status: [validStatus],
      history: [
        ...appointment.history,
        {
          status: validStatus,
          timestamp: new Date(),
          comments: "Appointment marked as No Show - Charge Fee",
          user: user._id,
        },
      ],
    };

    try {
      await dispatch(
        updateAppointment({
          id: appointment._id,
          updatedData,
        })
      ).unwrap();

      setShowNoShowModal(false);
      onAppointmentCancel();
    } catch (error) {
      console.error("Failed to mark appointment as No Show:", error);
    }
  };

  const handleNotifyClient = () => {
    dispatch(notifyClientByEmail({ appointment, user }));
    refetchAppointments(appointment.clinician._id);
    onEmailSent();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';

    let date;
    // Parse date in local timezone to avoid off-by-one day errors
    if (dateString instanceof Date) {
      // Already a Date object from MongoDB
      date = dateString;
    } else if (typeof dateString === 'string') {
      // Check if it already has time component
      if (dateString.includes('T')) {
        date = new Date(dateString);
      } else {
        date = new Date(dateString + 'T00:00:00');
      }
    } else {
      date = new Date(dateString);
    }

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return 'N/A';
    }

    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      weekday: "short",
      day: "numeric",
    });
  };

  const formatPrice = (price) => {
    if (typeof price !== "number" || isNaN(price)) {
      return "$0.00";
    }
    return `$${price.toFixed(2)}`;
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    const options = {
      year: "numeric",
      month: "short",
      weekday: "short",
      day: "numeric",
    };
    const timeOptions = {
      hour: "numeric",
      minute: "numeric",
      // second: "numeric",
      hour12: true,
    };
    return `${date.toLocaleDateString(
      "en-US",
      options
    )}, ${date.toLocaleTimeString("en-US", timeOptions)}`;
  };

  const formatClinicAddress = (address) => {
    if (typeof address === "string") {
      return address;
    }
    if (typeof address === "object" && address !== null) {
      const parts = [];
      if (address.street) parts.push(address.street);
      if (address.city) parts.push(address.city);
      if (address.province) parts.push(address.province);
      if (address.postalCode) parts.push(address.postalCode);
      if (address.country) parts.push(address.country);
      return parts.join(", ");
    }
    return "";
  };

  return (
    <div className="space-y-4">
      {/* Clinic Name - Full Width at Top */}
      {(type === "appointment" || type === "futureappointment") && appointment?.clinic && (
        <div className="w-full rounded-sm border border-gray-200 bg-blue-50 px-4 py-3 shadow-sm">
          <div className="text-center">
            <h3 className="text-lg font-semibold text-blue-900">
              {appointment.clinic.name}
            </h3>
            {appointment.clinic.address && (
              <p className="text-sm text-blue-700 mt-1">
                {formatClinicAddress(appointment.clinic.address)}
              </p>
            )}
          </div>
        </div>
      )}

      <div className="flex flex-col space-y-3">
        <div className="flex justify-between items-center">
          <h2 className="text-sm font-medium text-gray-900">
            {type === "client"
              ? "Client Info"
              : type === "appointment"
                ? "Appointment Info"
                : "Future Appointment Info"}
          </h2>

          {type === "appointment" && (
            <div className="px-1.5 py-1.5">
              <StatusLights appointment={selectedAppointment} />
            </div>
          )}
        </div>

        {type === "client" && client.parents.length > 0 && (
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => setShowParentsComponent(!showParentsComponent)}
              className="inline-flex items-center justify-center rounded-sm text-xs font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-1 focus:ring-gray-950 focus-visible:ring-offset-1 disabled:pointer-events-none disabled:opacity-50 bg-gray-900 text-white hover:bg-gray-800 h-8 px-3 py-2 shadow-sm">
              Parents
            </button>
          </div>
        )}

        <div className="mt-4">
          {(type === "appointment" || type === "futureappointment") && (
            <div className="overflow-x-auto -mx-4 px-4">
              <div className="min-w-max">
                <AppointmentButtonContainer
                  appointment={appointment}
                  onButtonClick={handleButtonClick}
                  showModifyBlock={!!appointment.blockId}
                />
              </div>
            </div>
          )}
        </div>

        <dl className="grid gap-3 sm:grid-cols-2">
          {/* Client Info Fields */}
          {type === "client" && client && (
            <>
              <div className="rounded-sm border border-gray-100/50 bg-white px-3 py-2 shadow-sm hover:shadow-md transition-all">
                <dt className="text-xs font-medium text-gray-500">Client Name</dt>
                <dd className="mt-1 text-sm text-gray-900">{`${selectedAppointment.client.firstName} ${selectedAppointment.client.lastName}`}</dd>
              </div>

              <div className="rounded-sm border border-gray-100/50 bg-white px-3 py-2 shadow-sm hover:shadow-md transition-all">
                <dt className="text-xs font-medium text-gray-500">Email</dt>
                <dd className="mt-1 text-sm text-gray-900 break-all">
                  {selectedAppointment.client.email}
                </dd>
              </div>

              <div className="rounded-sm border border-gray-100/50 bg-white px-3 py-2 shadow-sm hover:shadow-md transition-all">
                <dt className="text-xs font-medium text-gray-500">Phone Numbers</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {selectedAppointment.client.phoneNumbers &&
                    selectedAppointment.client.phoneNumbers.length > 0
                    ? selectedAppointment.client.phoneNumbers.map((phone) => (
                        <div key={phone._id} className="flex items-center space-x-2">
                          <span className="font-medium text-xs">{phone.type}:</span>
                          <span>{phone.number}</span>
                          {phone.note && (
                            <span className="text-gray-500 text-xs">({phone.note})</span>
                          )}
                        </div>
                      ))
                    : "N/A"}
                </dd>
              </div>

              <div className="rounded-sm border border-gray-100/50 bg-white px-3 py-2 shadow-sm hover:shadow-md transition-all">
                <dt className="text-xs font-medium text-gray-500">Date of Birth</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {new Date(
                    selectedAppointment.client.dateOfBirth + 'T00:00:00'
                  ).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </dd>
              </div>

              <div className="rounded-sm border border-gray-100/50 bg-white px-3 py-2 shadow-sm hover:shadow-md transition-all">
                <dt className="text-xs font-medium text-gray-500">Age</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {calculateAge(selectedAppointment.client.dateOfBirth)}
                </dd>
              </div>
            </>
          )}

          {/* Appointment Info Fields */}
          {(type === "appointment" || type === "futureappointment") && appointment && (
            <>
              <div className="rounded-sm border border-gray-100/50 bg-white px-3 py-2 shadow-sm hover:shadow-md transition-all">
                <dt className="text-xs font-medium text-gray-500">Service</dt>
                <dd className="mt-1 text-sm text-gray-900">{appointment.title}</dd>
              </div>

              <div className="rounded-sm border border-gray-100/50 bg-white px-3 py-2 shadow-sm hover:shadow-md transition-all">
                <dt className="text-xs font-medium text-gray-500">Cost</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {appointment?.service?.price ? formatPrice(appointment.service.price) : "N/A"}
                </dd>
              </div>

              <div className="rounded-sm border border-gray-100/50 bg-white px-3 py-2 shadow-sm hover:shadow-md transition-all">
                <dt className="text-xs font-medium text-gray-500">Date & Time</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {formatDateTime(appointment.start)}
                </dd>
              </div>

              <div
                className={`rounded-sm border border-gray-100/50 bg-white px-3 py-2 shadow-sm ${user?.role?.includes("owner") ? "cursor-pointer hover:bg-gray-50" : ""
                  }`}
                onClick={() => {
                  if (user?.role?.includes("owner")) {
                    setShowEditStatusModal(true);
                  }
                }}>
                <dt className="text-xs font-medium text-gray-500">Status</dt>
                <dd className="mt-1 space-y-1">
                  {appointment.status && appointment.status.length > 0 ? (
                    appointment.status.map((status, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center rounded-full px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 mr-2">
                        {status}
                      </span>
                    ))
                  ) : (
                    <span className="text-sm text-gray-500">
                      No statuses available
                    </span>
                  )}
                </dd>
              </div>

              <div 
                className="rounded-sm border border-gray-100/50 bg-white px-3 py-2 shadow-sm hover:shadow-md transition-all cursor-pointer"
                onClick={() => handleClinicianClick("treating")}>
                <dt className="text-xs font-medium text-gray-500">Treating Clinician</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {`${appointment.clinician.firstName} ${appointment.clinician.lastName}`}
                </dd>
              </div>

              <div 
                className="rounded-sm border border-gray-100/50 bg-white px-3 py-2 shadow-sm hover:shadow-md transition-all cursor-pointer"
                onClick={() => handleClinicianClick("supervising")}>
                <dt className="text-xs font-medium text-gray-500">Supervising Clinician</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {appointment.supervisingClinician
                    ? `${appointment.supervisingClinician.firstName} ${appointment.supervisingClinician.lastName}`
                    : "N/A"}
                </dd>
              </div>

              <div className="rounded-sm border border-gray-100/50 bg-white px-3 py-2 shadow-sm hover:shadow-md transition-all">
                <dt className="text-xs font-medium text-gray-500">Block Info</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {appointment.blockId ? (
                    <div
                      className="space-y-1 cursor-pointer transition-all"
                      onClick={() => handleButtonClick("modifyBlock")}>
                      {localBlockData?.title && (
                        <div className="font-medium">
                          {localBlockData.title}
                        </div>
                      )}
                      <div>
                        Session #{appointment.blockNumber}
                        {localBlockData?.appointments?.length && (
                          <span className="text-gray-500">
                            {" "}
                            / {localBlockData.appointments.length}
                          </span>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div
                      className="cursor-pointer hover:shadow-md hover:ring-1 hover:ring-indigo-100 p-3 rounded-sm transition-all text-sm text-gray-500"
                      onClick={() => handleButtonClick("addToBlock")}>
                      N/A
                    </div>
                  )}
                </dd>
              </div>

              <div className="rounded-sm border border-gray-100/50 bg-white px-3 py-2 shadow-sm hover:shadow-md transition-all">
                <dt className="text-xs font-medium text-gray-500">Resources</dt>
                <dd className="mt-1">
                  <div
                    className="cursor-pointer transition-all"
                    onClick={() => handleButtonClick("manageResources")}>
                    {appointment.resources &&
                    appointment.resources.length > 0 ? (
                      <div className="space-y-1">
                        {appointment.resources.map((resource) => (
                          <span
                            key={resource._id}
                            className="inline-flex items-center rounded-full px-2 py-1 text-xs font-medium bg-blue-50 text-blue-700 mr-2">
                            {resource.name}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-sm text-gray-500">None</span>
                    )}
                  </div>
                </dd>
              </div>
            </>
          )}
        </dl>
      </div>

      {showParentsComponent && <FamilyDetails display client={client} />}

      {showCancelModal && (
        <CancelAppointmentModal
          isOpen={showCancelModal}
          onClose={() => {
            setClinicianCancelComments("");
            setShowCancelModal(false);
          }}
          onCancelNoFee={handleConfirmCancelNoFee}
          onCancelWithFee={handleConfirmCancelWithFee}
          onClinicianCancel={handleClinicianCancel}
          clinicianCancelComments={clinicianCancelComments}
          setClinicianCancelComments={setClinicianCancelComments}
        />
      )}
      {showNoShowModal && (
        <NoShowAppointmentModal
          isOpen={showNoShowModal}
          onClose={() => setShowNoShowModal(false)}
          onNoShowNoFee={handleConfirmNoShowNoFee}
          onNoShowWithFee={handleConfirmNoShowWithFee}
        />
      )}
      {showQuickAddModal && selectedAppointment && (
        <QuickAddAppointmentModal
          isOpen={showQuickAddModal}
          onClose={() => {
            setShowQuickAddModal(false);
            dispatch(fetchAppointmentById(selectedAppointment._id));
          }}
          mode="edit"
          appointment={selectedAppointment}
          clinic={selectedAppointment.clinic}
        />
      )}

      {showModifyBlockModal && appointment.blockId && (
        <ModifyBlockModal
          isOpen={showModifyBlockModal}
          onClose={() => {
            dispatch(clearBlockState());
            setShowModifyBlockModal(false);
          }}
          blockId={appointment.blockId}
        />
      )}

      {showModifyStandaloneModal && !appointment.blockId && (
        <AddToBlockModal
          isOpen={showModifyStandaloneModal}
          onClose={() => setShowModifyStandaloneModal(false)}
          clientId={appointment.client?._id}
          standaloneAppointment={appointment}
        />
      )}

      {showModifyResourceModal && (
        <ModifyResourceModal
          isOpen={showModifyResourceModal}
          onClose={() => {
            setShowModifyResourceModal(false);
            dispatch(fetchAppointmentById(selectedAppointment._id));
          }}
          appointmentId={selectedAppointment._id}
        />
      )}

      {showSOAPForm && soapFormAppointment && (
        <SOAPForm
          onClose={() => setShowSOAPForm(false)}
          appointment={soapFormAppointment}
        />
      )}

      {showRescheduleModal && (
        <RescheduleModal
          isOpen={showRescheduleModal}
          onClose={() => {
            dispatch(fetchAppointmentById(rescheduleAppointment));
            setShowRescheduleModal(false);
          }}
          appointmentId={rescheduleAppointment}
        />
      )}

      {showTreatmentModal && (
        <TreatmentModal
          isOpen={showTreatmentModal}
          onClose={() => setShowTreatmentModal(false)}
          onSave={handleSubmitAttendance}
          parents={localParents}
        />
      )}

      {showEditStatusModal && (
        <EditStatusModal
          isOpen={showEditStatusModal}
          onClose={() => {
            setShowEditStatusModal(false);
            dispatch(fetchAppointmentById(selectedAppointment._id));
          }}
          appointment={selectedAppointment}
        />
      )}

      {showModifyClinicianModal && (
        <ModifyClinicianModal
          isOpen={showModifyClinicianModal}
          onClose={() => {
            setShowModifyClinicianModal(false);
            setClinicianModalType(null);
          }}
          appointment={selectedAppointment}
          client={selectedAppointment?.client}
          clinicianType={clinicianModalType}
          context="appointment"
          onSuccess={() => {
            // Refresh the appointment data after successful update
            if (selectedAppointment?._id) {
              dispatch(fetchAppointmentById(selectedAppointment._id));
            }
          }}
        />
      )}
    </div>
  );
}
